import FontFaceObserver from 'fontfaceobserver';
import {
  Autocomplete,
  ListSubheader,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from '@mui/material';
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  Italic,
  Minus,
  Plus,
  Underline,
} from 'react-feather';
import styled from 'styled-components';
import { fontFamilies, fontGroups } from '../../config';
import { editorTheme } from '../../theme';
import TextColor from './TextColor';
import TextSpacing from './TextSpacing';
import { Separator, ToolButton } from './ToolbarControls';
import { sharedValue, useSelectedText } from './useSelectedText';

const FONT_SIZES = [
  20, 30, 40, 50, 60, 70, 80, 90, 100, 110, 120, 140, 160, 180, 200, 220, 240,
  260, 280, 360,
];
const MIN_FONT_SIZE = 8;
const MAX_FONT_SIZE = 1000;

// Text is as wide as its longest line, so alignment places the lines of
// multi-line text relative to each other. Not 'justify': players lay text
// out with preserved line breaks, which browsers never justify.
const ALIGNMENTS = [
  { value: 'left', label: 'Align text left', Icon: AlignLeft },
  { value: 'center', label: 'Align text center', Icon: AlignCenter },
  { value: 'right', label: 'Align text right', Icon: AlignRight },
];

const SizeField = styled.div`
  height: 32px;
  flex: none;
  display: flex;
  align-items: center;
  border: 1px solid ${editorTheme.border};
  border-radius: 6px;
  & > button {
    width: 26px;
    height: 30px;
  }
`;

const isBold = (fontWeight: fabric.IText['fontWeight']) =>
  fontWeight === 'bold' || Number(fontWeight) >= 600;

const TextToolbar = () => {
  const { objects, apply } = useSelectedText();
  if (!objects.length) return null;

  const font = sharedValue(objects, (object) => object.fontFamily || '');
  const fontSize = sharedValue(objects, (object) => object.fontSize || 0);
  const bold = objects.every((object) => isBold(object.fontWeight));
  const italic = objects.every((object) => object.fontStyle === 'italic');
  const underline = objects.every((object) => !!object.underline);
  const textAlign = sharedValue(objects, (object) => object.textAlign);

  // Text is measured with the font it uses, so the font is applied once it
  // has loaded.
  const setFont = (family: string) =>
    new FontFaceObserver(family)
      .load()
      .then(() => apply({ fontFamily: family }));

  const setFontSize = (value: number) => {
    if (!Number.isFinite(value) || value <= 0) return;
    apply({
      fontSize: Math.min(
        MAX_FONT_SIZE,
        Math.max(MIN_FONT_SIZE, Math.round(value)),
      ),
    });
  };

  const nextSize = fontSize && FONT_SIZES.find((size) => size > fontSize);
  const previousSize =
    fontSize && [...FONT_SIZES].reverse().find((size) => size < fontSize);

  return (
    <>
      <Select
        value={font || ''}
        size="small"
        displayEmpty
        inputProps={{ 'aria-label': 'Font' }}
        sx={{ minWidth: 168, height: 32, fontSize: 13, flex: 'none' }}
        renderValue={(value) =>
          value ? (
            <span style={{ fontFamily: value }}>{value}</span>
          ) : (
            'Several fonts'
          )
        }
        MenuProps={{ PaperProps: { sx: { maxHeight: 420 } } }}
        onChange={(event) => setFont(event.target.value)}
      >
        {/* Text can use a font that is no longer offered. */}
        {font && !fontFamilies.includes(font) && (
          <MenuItem value={font} sx={{ fontFamily: font }}>
            {font}
          </MenuItem>
        )}
        {fontGroups.map((group) => [
          <ListSubheader key={group.label}>{group.label}</ListSubheader>,
          ...group.fonts.map((family) => (
            <MenuItem key={family} value={family} sx={{ fontFamily: family }}>
              {family}
            </MenuItem>
          )),
        ])}
      </Select>
      <Separator />
      <SizeField>
        <ToolButton
          aria-label="Decrease font size"
          disabled={!previousSize}
          onClick={() => previousSize && setFontSize(previousSize)}
        >
          <Minus size={14} />
        </ToolButton>
        <Autocomplete
          options={FONT_SIZES.map(String)}
          value={fontSize ? String(fontSize) : ''}
          onChange={(_, value) => value && setFontSize(Number(value))}
          freeSolo
          autoSelect
          disableClearable
          forcePopupIcon={false}
          size="small"
          sx={{
            width: 52,
            '& .MuiOutlinedInput-root': { p: 0, height: 30, fontSize: 13 },
            '& .MuiOutlinedInput-root .MuiAutocomplete-input': {
              p: 0,
              textAlign: 'center',
            },
            '& fieldset': { border: 'none' },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              inputProps={{
                ...params.inputProps,
                inputMode: 'numeric',
                'aria-label': 'Font size',
              }}
            />
          )}
        />
        <ToolButton
          aria-label="Increase font size"
          disabled={!nextSize}
          onClick={() => nextSize && setFontSize(nextSize)}
        >
          <Plus size={14} />
        </ToolButton>
      </SizeField>
      <TextColor />
      <Separator />
      <Tooltip title="Bold">
        <ToolButton
          aria-label="Bold"
          aria-pressed={bold}
          active={bold}
          onClick={() => apply({ fontWeight: bold ? 'normal' : 'bold' })}
        >
          <Bold size={16} />
        </ToolButton>
      </Tooltip>
      <Tooltip title="Italic">
        <ToolButton
          aria-label="Italic"
          aria-pressed={italic}
          active={italic}
          onClick={() => apply({ fontStyle: italic ? 'normal' : 'italic' })}
        >
          <Italic size={16} />
        </ToolButton>
      </Tooltip>
      <Tooltip title="Underline">
        <ToolButton
          aria-label="Underline"
          aria-pressed={underline}
          active={underline}
          onClick={() => apply({ underline: !underline })}
        >
          <Underline size={16} />
        </ToolButton>
      </Tooltip>
      <Separator />
      <div
        role="radiogroup"
        aria-label="Text alignment"
        style={{ display: 'flex' }}
      >
        {ALIGNMENTS.map(({ value, label, Icon }) => (
          <Tooltip key={value} title={label}>
            <ToolButton
              role="radio"
              aria-label={label}
              aria-checked={textAlign === value}
              active={textAlign === value}
              onClick={() => apply({ textAlign: value })}
            >
              <Icon size={16} />
            </ToolButton>
          </Tooltip>
        ))}
      </div>
      <TextSpacing />
    </>
  );
};

export default TextToolbar;
